/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { useTranslation } from 'react-i18next'
import { Company } from 'types/entities/Company'
import { EditCompanyTab } from 'types/entities/specific/EditCompanyTab'
import { EditCompanyModalTab } from 'types/enums/specific/EditCompanyModalTab'
import { countCompanyBookings } from 'utils/companiesUtils'
import { splitLeadsAndOpportunities } from 'utils/leadOpportunityUtils'

const getCount = <T,>(items?: T[] | null): number => items?.length ?? 0

const getRelationsCount = (company: Company) => {
    const split = splitLeadsAndOpportunities(company.leads ?? [])

    return {
        leads: split.leads.length,
        opportunities: split.opportunities.length,
        offers: getCount(company.offers),
        reservations: countCompanyBookings(company.reservations),
    }
}

export const useEditCompanyTabs = (company: Company | null): EditCompanyTab[] => {
    const { t } = useTranslation()

    if (!company) {
        return []
    }

    const counts = getRelationsCount(company)

    return [
        { id: EditCompanyModalTab.Company, name: t('Company') },
        {
            id: EditCompanyModalTab.Leads,
            name: `${t('Leads')} (${counts.leads})`,
        },
        {
            id: EditCompanyModalTab.Opportunities,
            name: `${t('Opportunities')} (${counts.opportunities})`,
        },
        {
            id: EditCompanyModalTab.Offers,
            name: `${t('Offers')} (${counts.offers})`,
        },
        {
            id: EditCompanyModalTab.Reservations,
            name: `${t('Reservations')} (${counts.reservations})`,
        },
        {
            id: EditCompanyModalTab.ActivityLog,
            name: `${t('Activity Log')} (${counts.leads + counts.opportunities + counts.offers})`,
        },
    ]
}
